if (!customElements.get('piume-completa-look')) {
  customElements.define('piume-completa-look', class extends HTMLElement {
    connectedCallback() {
      this.observer?.disconnect();

      this.observer = new MutationObserver(() => this.initialize());
      this.observer.observe(this, {
        childList: true,
        subtree: true
      });

      this.initialize();
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;

      const configNode = this.querySelector('[data-look-config]');
      const button = this.querySelector('[data-look-add]');
      const total = this.querySelector('[data-look-total]');
      const summary = this.querySelector('[data-look-summary]');
      const error = this.querySelector('[data-look-error]');

      if (!configNode || !button || !total || !summary || !error) return;

      const text = configNode.content
        ? configNode.content.textContent
        : configNode.textContent;

      let config;

      try {
        config = JSON.parse(text);
      } catch (err) {
        error.textContent = 'Impossibile leggere i dati del look.';
        error.hidden = false;
        return;
      }

      if (!Array.isArray(config.variants)) {
        error.textContent = 'Mancano i dati delle varianti.';
        error.hidden = false;
        return;
      }

      this.config = config;
      this.button = button;
      this.total = total;
      this.summary = summary;
      this.error = error;
      this.initialized = true;

      this.observer.disconnect();

      this.onChange = () => this.update();
      this.onClick = () => this.add();

      this.addEventListener('change', this.onChange);
      this.button.addEventListener('click', this.onClick);

      this.timer = window.setInterval(() => this.update(), 500);
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.clearInterval(this.timer);

      if (this.onChange) {
        this.removeEventListener('change', this.onChange);
      }

      if (this.button && this.onClick) {
        this.button.removeEventListener('click', this.onClick);
      }

      this.initialized = false;
    }

    money(cents) {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'it-IT',
        {
          style: 'currency',
          currency: this.config.currency || 'EUR'
        }
      ).format(cents / 100);
    }

    getState() {
      const form = this.config.formId
        ? document.getElementById(this.config.formId)
        : null;

      if (!(form instanceof HTMLFormElement)) {
        return { form: null, variant: null, quantity: 1 };
      }

      const data = new FormData(form);
      const idField = form.elements.namedItem('id');
      const variantId = String(
        data.get('id') || idField?.value || ''
      );

      const variant = this.config.variants.find(
        item => String(item.id) === variantId
      );

      const rawQuantity = Number(data.get('quantity') || 1);
      const quantity = Number.isInteger(rawQuantity) && rawQuantity > 0
        ? rawQuantity
        : 1;

      return {
        form,
        data,
        variant,
        quantity,
        unavailable: Boolean(idField?.disabled)
      };
    }

    selectedItems() {
      const items = [];

      this.querySelectorAll('[data-look-item]').forEach(item => {
        const checkbox = item.querySelector('[data-look-check]');
        const select = item.querySelector('[data-look-select]');
        const option = select?.selectedOptions[0];

        if (
          checkbox?.checked &&
          !checkbox.disabled &&
          !select?.disabled &&
          option &&
          !option.disabled &&
          option.value
        ) {
          items.push({
            id: option.value,
            quantity: 1,
            price: Number(option.dataset.price)
          });
        }
      });

      return items;
    }

    update() {
      if (!this.initialized) return;

      this.querySelectorAll('[data-look-item]').forEach(item => {
        const checkbox = item.querySelector('[data-look-check]');
        const select = item.querySelector('[data-look-select]');
        const panel = item.querySelector('[data-look-options]');
        const price = item.querySelector('[data-look-price]');
        const option = select?.selectedOptions[0];

        if (panel && checkbox) {
          panel.hidden = !checkbox.checked;
          item.classList.toggle('is-selected', checkbox.checked);
        }

        if (price && option) {
          price.textContent = this.money(Number(option.dataset.price));
        }
      });

      const state = this.getState();
      const extras = this.selectedItems();

      const validMain = Boolean(
        state.variant?.available && !state.unavailable
      );

      if (state.variant) {
        const total =
          state.variant.price * state.quantity +
          extras.reduce((sum, item) => sum + item.price, 0);

        this.total.textContent = this.money(total);

        const variantTitle = state.variant.title === 'Default Title'
          ? 'Variante unica'
          : state.variant.title;

        this.summary.textContent =
          `${this.config.title} · ${variantTitle}` +
          (state.quantity > 1 ? ` · Quantità ${state.quantity}` : '');
      } else {
        this.total.textContent = '—';

        this.summary.textContent = state.form
          ? 'Seleziona la variante del prodotto principale.'
          : 'Collegamento al prodotto principale non trovato.';
      }

      this.button.disabled = Boolean(
        this.busy || !validMain || !extras.length
      );

      if (!this.busy) {
        this.button.textContent = !validMain
          ? 'SELEZIONA UNA VARIANTE DISPONIBILE'
          : !extras.length
            ? 'SELEZIONA UN ABBINAMENTO'
            : 'ACQUISTA IL LOOK';
      }
    }

    async add() {
      if (this.busy) return;

      const state = this.getState();
      const extras = this.selectedItems();

      if (
        !state.form ||
        !state.variant?.available ||
        state.unavailable ||
        !extras.length
      ) {
        this.update();
        return;
      }

      if (!state.form.reportValidity()) return;

      const mainItem = {
        id: state.variant.id,
        quantity: state.quantity
      };

      const properties = {};

      for (const [key, value] of state.data.entries()) {
        const match = key.match(/^properties\[(.*)\]$/);

        if (match && typeof value === 'string') {
          properties[match[1]] = value;
        }
      }

      if (Object.keys(properties).length) {
        mainItem.properties = properties;
      }

      const sellingPlan = state.data.get('selling_plan');

      if (sellingPlan) {
        mainItem.selling_plan = sellingPlan;
      }

      const items = [
        mainItem,
        ...extras.map(item => ({
          id: item.id,
          quantity: item.quantity
        }))
      ];

      this.busy = true;
      this.error.hidden = true;
      this.button.disabled = true;
      this.button.textContent = 'AGGIUNTA IN CORSO…';

      const root = window.Shopify?.routes?.root || '/';

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
            result.message ||
            'Non è stato possibile aggiungere il look.'
          );
        }

        window.location.assign(`${root}cart`);
      } catch (err) {
        this.error.textContent = err.message;
        this.error.hidden = false;
        this.busy = false;
        this.update();
      }
    }
  });
}